import React, { useMemo, useState } from "react";
import { X, Check, Prohibit, PencilSimple, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

// Currency-aware money formatter (all P&L is USD in prop-sim state).
const fmtMoney = (n, sign = false) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  const s = sign && v > 0 ? "+" : v < 0 ? "-" : "";
  return `${s}${Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmt = (n, d = 2) => (n === null || n === undefined || Number.isNaN(Number(n))) ? "—" :
  Number(n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });

// Emoji flag per instrument type — matches Exness's tiny country badge.
function InstrumentDot({ symbol }) {
  const s = symbol.toUpperCase();
  let bg = "bg-secondary text-foreground", char = s.slice(0, 1);
  if (s.startsWith("BTC")) { bg = "bg-orange-500 text-white"; char = "₿"; }
  else if (s.startsWith("ETH")) { bg = "bg-indigo-500 text-white"; char = "Ξ"; }
  else if (s.startsWith("XAU") || s === "MCX_GOLD") { bg = "bg-yellow-500 text-black"; char = "Au"; }
  else if (s.startsWith("XAG") || s === "MCX_SILVER") { bg = "bg-slate-300 text-black"; char = "Ag"; }
  else if (s.includes("OIL") || s === "MCX_CRUDEOIL") { bg = "bg-neutral-800 text-white border border-white/20"; char = "◑"; }
  return (
    <div className={`w-7 h-7 rounded-full grid place-items-center text-[11px] font-bold ${bg} shrink-0`}>{char}</div>
  );
}

// Inline SL/TP modify strip that expands under a card when Modify is tapped.
function InlineModify({ trade, meta, onDone }) {
  const m = meta || {};
  const decimals = m.decimals ?? 4;
  const isLong = trade.side === "long";
  const cur = Number(trade.current_price || trade.entry_price);
  const step = decimals === 0 ? 1 : Math.pow(10, -decimals);
  const bump = cur * 0.005;
  const [sl, setSl] = useState(trade.stop_loss ?? Number((isLong ? cur - bump : cur + bump).toFixed(decimals)));
  const [tp, setTp] = useState(trade.take_profit ?? Number((isLong ? cur + bump : cur - bump).toFixed(decimals)));
  const [busy, setBusy] = useState(false);

  const commit = async () => {
    setBusy(true);
    try {
      const patches = [];
      if (Number(sl) !== Number(trade.stop_loss)) {
        patches.push(api.post(`/prop-sim/trade/${trade.id}/sltp`, { stop_loss: Number(sl) || null }));
      }
      if (Number(tp) !== Number(trade.take_profit)) {
        patches.push(api.post(`/prop-sim/trade/${trade.id}/sltp`, { take_profit: Number(tp) || null }));
      }
      const results = await Promise.all(patches);
      const last = results[results.length - 1];
      toast.success("SL/TP updated");
      onDone(last?.data?.state);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const Row = ({ label, value, setValue, tone }) => (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{label}</p>
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => setValue(Number((Number(value) - step).toFixed(decimals)))}
          className="w-8 h-9 rounded-md bg-secondary hover:bg-secondary/70 font-bold text-lg"
          data-testid={`mp-modify-${label.toLowerCase()}-dec`}>−</button>
        <input
          type="number"
          step={step}
          value={value || ""}
          onChange={(e) => setValue(Number(e.target.value))}
          className={`flex-1 min-w-0 h-9 px-2 rounded-md bg-secondary/40 border border-border text-center font-mono-num text-sm ${tone}`}
          data-testid={`mp-modify-${label.toLowerCase()}-input`} />
        <button
          onClick={() => setValue(Number((Number(value) + step).toFixed(decimals)))}
          className="w-8 h-9 rounded-md bg-secondary hover:bg-secondary/70 font-bold text-lg"
          data-testid={`mp-modify-${label.toLowerCase()}-inc`}>+</button>
      </div>
    </div>
  );

  return (
    <div className="mt-2 p-3 rounded-lg bg-background border border-border space-y-3" data-testid="mp-inline-modify">
      <div className="grid grid-cols-2 gap-3">
        <Row label="SL" value={sl} setValue={setSl} tone="text-red-400" />
        <Row label="TP" value={tp} setValue={setTp} tone="text-emerald-400" />
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onDone(null)}
          className="flex-1 h-9 rounded-md border border-border text-sm text-muted-foreground hover:text-foreground"
          data-testid="mp-modify-cancel">Cancel</button>
        <button
          onClick={commit}
          disabled={busy}
          className="flex-1 h-9 rounded-md bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-1"
          data-testid="mp-modify-save">
          <Check size={14} weight="bold" /> {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function OpenCard({ t, meta, onStateUpdate }) {
  const m = (meta || []).find(s => s.symbol === t.symbol) || {};
  const decimals = m.decimals ?? 4;
  const isLong = t.side === "long";
  const positive = (t.unrealized_pnl || 0) >= 0;
  const [modifyOpen, setModifyOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [showPartial, setShowPartial] = useState(false);

  const closePct = async (pct) => {
    if (closing) return;
    setClosing(true);
    setShowPartial(false);
    const step = 0.01;
    const qty = pct >= 100 ? null : Math.max(step, Math.round(t.quantity * (pct / 100) * 100) / 100);
    try {
      const { data } = await api.post(`/prop-sim/close/${t.id}`, qty != null ? { qty } : {});
      onStateUpdate(data.state);
      toast.success(
        data.partial
          ? `Partial · ${fmt(data.closed_qty, 2)} lots · ${fmtMoney(data.pnl, true)}`
          : `Closed ${t.symbol} · ${fmtMoney(data.pnl, true)}`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Close failed");
    } finally { setClosing(false); }
  };

  return (
    <div className="rounded-xl bg-secondary border border-border p-3" data-testid={`mp-open-card-${t.id}`}>
      <div className="flex items-center gap-2">
        <InstrumentDot symbol={t.symbol} />
        <p className="font-semibold text-sm text-foreground">{t.symbol}</p>
        {t.stop_loss ? (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 font-semibold" title="Stop-loss attached">SL</span>
        ) : null}
        {t.take_profit ? (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-semibold" title="Take-profit attached">TP</span>
        ) : null}
        <p className={`ml-auto font-mono-num text-sm font-bold ${positive ? "text-emerald-400" : "text-red-400"}`}
          data-testid={`mp-open-pnl-${t.id}`}>
          {fmtMoney(t.unrealized_pnl, true)} <span className="text-muted-foreground text-[10px]">USD</span>
        </p>
        <button
          onClick={() => setShowPartial(v => !v)}
          disabled={closing}
          title="Close position"
          className="p-1 rounded-full text-muted-foreground hover:text-red-400 disabled:opacity-40"
          data-testid={`mp-close-btn-${t.id}`}>
          <Prohibit size={18} weight="bold" />
        </button>
      </div>
      {showPartial && (
        <div className="mt-2 flex items-center gap-1.5" data-testid={`mp-partial-chips-${t.id}`}>
          {[25, 50, 75, 100].map(pct => (
            <button key={pct}
              onClick={() => closePct(pct)}
              disabled={closing}
              className={`flex-1 text-[11px] py-1.5 rounded-md border border-border font-medium
                ${pct === 100 ? "bg-red-500/10 text-red-400 hover:bg-red-500/20" : "bg-secondary hover:bg-background text-foreground"}`}
              data-testid={`mp-partial-${t.id}-${pct}`}>
              {pct === 100 ? "All" : `${pct}%`}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between mt-1 pl-9">
        <p className="text-xs">
          <span className={isLong ? "text-sky-400" : "text-red-400"}>{isLong ? "Buy" : "Sell"}</span>
          <span className="text-muted-foreground"> {fmt(t.quantity, 2)} lots at {fmt(t.entry_price, decimals)}</span>
        </p>
        <p className="text-xs font-mono-num text-muted-foreground">{fmt(t.current_price, decimals)}</p>
      </div>
      <div className="flex items-center justify-end mt-2 pl-9">
        <button
          onClick={() => setModifyOpen(v => !v)}
          className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
          data-testid={`mp-modify-toggle-${t.id}`}>
          <PencilSimple size={12} weight="bold" /> {modifyOpen ? "Close editor" : "Modify SL / TP"}
        </button>
      </div>
      {modifyOpen && (
        <InlineModify trade={t} meta={m} onDone={(newState) => {
          setModifyOpen(false);
          if (newState) onStateUpdate(newState);
        }} />
      )}
    </div>
  );
}

function PendingCard({ o, meta, onStateUpdate }) {
  const m = (meta || []).find(s => s.symbol === o.symbol) || {};
  const decimals = m.decimals ?? 4;
  const isLong = o.side === "long";
  const [busy, setBusy] = useState(false);

  const cancel = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/prop-sim/limit-order/${o.id}/cancel`);
      onStateUpdate(data.state);
      toast.success("Order cancelled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl bg-secondary border border-border p-3" data-testid={`mp-pending-card-${o.id}`}>
      <div className="flex items-center gap-2">
        <InstrumentDot symbol={o.symbol} />
        <p className="font-semibold text-sm text-foreground">{o.symbol}</p>
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-primary/15 text-primary font-semibold uppercase">
          {o.order_type || "Limit"}
        </span>
        <p className="ml-auto text-xs font-mono-num text-muted-foreground">
          @ {fmt(o.trigger_price, decimals)}
        </p>
        <button
          onClick={cancel}
          disabled={busy}
          title="Cancel pending order"
          className="p-1 rounded-full text-muted-foreground hover:text-red-400 disabled:opacity-40"
          data-testid={`mp-cancel-btn-${o.id}`}>
          <Prohibit size={18} weight="bold" />
        </button>
      </div>
      <div className="flex items-center justify-between mt-1 pl-9">
        <p className="text-xs">
          <span className={isLong ? "text-sky-400" : "text-red-400"}>{isLong ? "Buy" : "Sell"}</span>
          <span className="text-muted-foreground"> {fmt(o.quantity, 2)} lots</span>
        </p>
        {o.stop_loss || o.take_profit ? (
          <p className="text-[10px] text-muted-foreground">
            {o.stop_loss ? <><span className="text-red-400">SL {fmt(o.stop_loss, decimals)}</span></> : null}
            {o.stop_loss && o.take_profit ? " · " : ""}
            {o.take_profit ? <><span className="text-emerald-400">TP {fmt(o.take_profit, decimals)}</span></> : null}
          </p>
        ) : <span />}
      </div>
    </div>
  );
}

function ClosedCard({ t, meta }) {
  const m = (meta || []).find(s => s.symbol === t.symbol) || {};
  const decimals = m.decimals ?? 4;
  const isLong = t.side === "long";
  const positive = (t.pnl || 0) >= 0;
  return (
    <div className="rounded-xl bg-secondary border border-border p-3" data-testid={`mp-closed-card-${t.id}`}>
      <div className="flex items-center gap-2">
        <InstrumentDot symbol={t.symbol} />
        <p className="font-semibold text-sm text-foreground">{t.symbol}</p>
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase">Closed</span>
        <p className={`ml-auto font-mono-num text-sm font-bold ${positive ? "text-emerald-400" : "text-red-400"}`}>
          {fmtMoney(t.pnl, true)} <span className="text-muted-foreground text-[10px]">USD</span>
        </p>
      </div>
      <div className="flex items-center justify-between mt-1 pl-9">
        <p className="text-xs">
          <span className={isLong ? "text-sky-400" : "text-red-400"}>{isLong ? "Buy" : "Sell"}</span>
          <span className="text-muted-foreground"> {fmt(t.quantity, 2)} lots @ {fmt(t.entry_price, decimals)}</span>
        </p>
        <p className="text-xs font-mono-num text-muted-foreground">{fmt(t.exit_price, decimals)}</p>
      </div>
      <p className="text-[10px] text-muted-foreground pl-9 mt-0.5 truncate">
        {t.exit_reason ? `${t.exit_reason} · ` : ""}{(t.exit_time || "").replace("T", " ").slice(0, 16)}
      </p>
    </div>
  );
}

export default function MobilePortfolioSheet({ state, symbols, onStateUpdate, triggerLabel = null }) {
  const [tab, setTab] = useState("open");
  const [closeMenuOpen, setCloseMenuOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const open = state?.open_trades || [];
  const pending = state?.pending_orders || [];
  const closed = state?.closed_trades || [];
  const winners = open.filter(t => (t.unrealized_pnl || 0) >= 0).length;
  const losers = open.length - winners;

  const totalMargin = useMemo(() => {
    return open.reduce((sum, t) => {
      const m = (symbols || []).find(s => s.symbol === t.symbol) || {};
      const cs = Number(m.contract_size || 1);
      const lev = Number(t.leverage || m.leverage_max || m.leverage || 200);
      const px = Number(t.entry_price || 0);
      const qty = Number(t.quantity || 0);
      // For INR-quoted MCX symbols, normalise notional to USD (~89 INR/USD).
      const usdMul = m.currency === "INR" ? (1 / 89) : 1;
      const notional = qty * cs * px * usdMul;
      return sum + (lev > 0 ? notional / lev : 0);
    }, 0);
  }, [open, symbols]);

  const doBulk = async (path, label) => {
    setBulkBusy(true);
    setCloseMenuOpen(false);
    try {
      const { data } = await api.post(path);
      onStateUpdate(data.state);
      toast.success(`${label} · ${data.closed || 0} position${data.closed !== 1 ? "s" : ""}`);
    } catch {
      toast.error(`${label} failed`);
    } finally { setBulkBusy(false); }
  };

  const listContent = () => {
    if (tab === "open") {
      if (open.length === 0) {
        return <div className="text-center text-sm text-muted-foreground py-10" data-testid="mp-open-empty">No open positions.</div>;
      }
      return open.map(t => <OpenCard key={t.id} t={t} meta={symbols} onStateUpdate={onStateUpdate} />);
    }
    if (tab === "pending") {
      if (pending.length === 0) {
        return <div className="text-center text-sm text-muted-foreground py-10" data-testid="mp-pending-empty">No pending orders.</div>;
      }
      return pending.map(o => <PendingCard key={o.id} o={o} meta={symbols} onStateUpdate={onStateUpdate} />);
    }
    if (closed.length === 0) {
      return <div className="text-center text-sm text-muted-foreground py-10" data-testid="mp-closed-empty">No closed trades yet.</div>;
    }
    return closed
      .slice()
      .sort((a, b) => (b.exit_time || "").localeCompare(a.exit_time || ""))
      .slice(0, 50)
      .map(t => <ClosedCard key={t.id} t={t} meta={symbols} />);
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          data-testid="propsim-mobile-portfolio-fab"
          className="rounded-full bg-secondary/90 text-foreground border border-border shadow-xl px-4 py-3 font-semibold text-sm flex items-center gap-2 hover:bg-secondary transition-colors backdrop-blur">
          Portfolio
          {open.length + pending.length > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground font-bold">
              {open.length + pending.length}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        style={{ zIndex: 300 }}
        className="p-0 h-[92vh] max-h-[92vh] rounded-t-2xl border-t border-border bg-background flex flex-col"
        data-testid="propsim-mobile-portfolio-sheet">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-border">
          <p className="font-display text-lg font-semibold text-foreground">Portfolio</p>
        </div>

        {/* Tabs */}
        <div className="mx-4 mt-3 rounded-lg border border-border p-1 grid grid-cols-3 text-sm">
          {[
            ["open", `Open ${open.length ? `(${open.length})` : ""}`.trim()],
            ["pending", `Pending ${pending.length ? `(${pending.length})` : ""}`.trim()],
            ["closed", "Closed"],
          ].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`h-9 rounded-md font-medium transition-colors ${
                tab === k ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`mp-tab-${k}`}>
              {l}
            </button>
          ))}
        </div>

        {/* Summary block (Open tab only) */}
        {tab === "open" && (
          <div className="px-4 pt-4">
            <div className="flex justify-between text-sm">
              <p className="text-muted-foreground">Total margin:</p>
              <p className="font-mono-num" data-testid="mp-total-margin">
                {fmtMoney(totalMargin)} <span className="text-muted-foreground text-xs">USD</span>
              </p>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <p className="text-muted-foreground">Total P/L:</p>
              <p className={`font-mono-num font-semibold ${(state?.unrealized_pnl || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}
                data-testid="mp-total-pnl">
                {fmtMoney(state?.unrealized_pnl, true)} <span className="text-muted-foreground text-xs font-normal">USD</span>
              </p>
            </div>

            {/* Close-all menu */}
            <div className="flex justify-end mt-3 relative">
              <button
                onClick={() => setCloseMenuOpen(v => !v)}
                disabled={open.length === 0 || bulkBusy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-secondary/80 hover:bg-secondary text-sm disabled:opacity-40"
                data-testid="mp-close-all-menu">
                Close all <CaretDown size={12} weight="bold" />
              </button>
              {closeMenuOpen && (
                <div className="absolute right-0 top-10 z-10 w-56 rounded-lg border border-border bg-card shadow-2xl overflow-hidden"
                  data-testid="mp-close-all-dropdown">
                  <button
                    onClick={() => doBulk("/prop-sim/close-all", "Closed all")}
                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-secondary flex items-center justify-between">
                    <span>Close all</span>
                    <span className="text-xs text-muted-foreground">{open.length}</span>
                  </button>
                  <button
                    onClick={() => doBulk("/prop-sim/close-profitable", "Closed winners")}
                    disabled={winners === 0}
                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-secondary flex items-center justify-between disabled:opacity-40 border-t border-border">
                    <span className="text-emerald-400">Close winners</span>
                    <span className="text-xs text-muted-foreground">{winners}</span>
                  </button>
                  <button
                    onClick={() => doBulk("/prop-sim/close-losing", "Closed losers")}
                    disabled={losers === 0}
                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-secondary flex items-center justify-between disabled:opacity-40 border-t border-border">
                    <span className="text-red-400">Close losers</span>
                    <span className="text-xs text-muted-foreground">{losers}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="mp-list">
          {listContent()}
        </div>
      </SheetContent>
    </Sheet>
  );
}
